import { useCallback, useEffect, useMemo, useState } from "react";
import {
  COMMUNICATION_SCOPE,
  COMMUNICATION_SCOPE_VALUES,
  DEFAULT_AGENT_TIMEZONE,
  DEFAULT_EXTERNAL_COMMUNICATION_CONFIG,
  EMAIL_PROVIDER,
  isExternalInboundEnabled,
  isExternalOutboundEnabled,
  OUTBOUND_APPROVAL,
  QUOTE_ITEM_CREATION_POLICY,
  QUOTE_REVIEWER_ASSIGNMENT_MODE,
  UpsertStandardAgentSettingsBody,
} from "@forgepoint/shared";
import type {
  CommunicationScope,
  EmailProvider,
  ExternalCommunicationConfig,
  ItemCreationPolicy,
  QuoteReviewerAssignmentMode,
} from "@forgepoint/shared";
import { Check, ChevronsUpDown, Pencil } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConnectionSelector } from "@/components/ConnectionSelector";
import { UserSelector } from "@/components/UserSelector";
import { getTimezoneList } from "@/components/cron/cronUtils";
import { fetchApi } from "@/lib/api";
import { useStandardAgentSettingsStore } from "@/store/standardAgentSettingsStore";
import { useHasPermission } from "@/store/authStore";
import { Permission } from "@forgepoint/shared";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SettingsRow } from "./components/SettingsRow";
import {
  EmailConfigSheet,
  type EmailConfigValues,
} from "./components/EmailConfigSheet";
import { TextSettingSheet } from "./components/TextSettingSheet";
import { SopFieldsSheet } from "./components/SopFieldsSheet";
import { UnsavedChangesBar } from "./components/UnsavedChangesBar";
import { ExtractionSchemaDialog } from "./components/ExtractionSchemaDialog";
import { StandardAgentTriggersCard } from "./components/StandardAgentTriggersCard";
import { SopSyncBanner } from "./components/SopSyncBanner";
import { useOrganizationStore } from "@/store/organizationStore";
import { useFeatureFlag } from "@/store/featureFlagStore";

const EMAIL_PROVIDER_LABELS: Record<EmailProvider, string> = {
  [EMAIL_PROVIDER.AGENT_EMAIL]: "Agent Email (Outlook)",
  [EMAIL_PROVIDER.FORGEPOINT_MAIL]: "Forgepoint Mail",
  [EMAIL_PROVIDER.GMAIL]: "Gmail",
  [EMAIL_PROVIDER.MICROSOFT_OUTLOOK]: "Microsoft Outlook",
  [EMAIL_PROVIDER.SMTP]: "SMTP",
};

/** Human-readable labels for the per-direction communication reach options. */
const COMMUNICATION_SCOPE_LABELS: Record<CommunicationScope, string> = {
  [COMMUNICATION_SCOPE.USER]: "Org accounts only",
  [COMMUNICATION_SCOPE.INTERNAL]: "Internal domains",
  [COMMUNICATION_SCOPE.EXTERNAL]: "Anyone (external)",
};

/**
 * Main settings panel for configuring the standard agent.
 * Grouped by skills with a vertical list layout, using sheets for complex/text configuration.
 * @returns Agent settings panel element
 */
export function AgentSettingsPanel() {
  const settings = useStandardAgentSettingsStore((s) => s.settings);
  const isLoading = useStandardAgentSettingsStore((s) => s.isLoading);
  const error = useStandardAgentSettingsStore((s) => s.error);
  const fetchSettings = useStandardAgentSettingsStore((s) => s.fetchSettings);
  const updateSettings = useStandardAgentSettingsStore((s) => s.updateSettings);
  const organizationSlug = useOrganizationStore((s) => s.organization?.slug);
  const fetchOrganization = useOrganizationStore((s) => s.fetchOrganization);
  const demandForecastingFeatureEnabled = useFeatureFlag("demand-forecasting");
  const canEdit = useHasPermission(Permission.SETTINGS_EDIT);

  // ── Form state ──
  const [agentName, setAgentName] = useState("");
  const [timezone, setTimezone] = useState(DEFAULT_AGENT_TIMEZONE);
  const [internalEmailDomainsInput, setInternalEmailDomainsInput] =
    useState("");
  const [requirements, setRequirements] = useState("");
  const [secondaryInformationToRequest, setSecondaryInformationToRequest] =
    useState("");
  const [quotingSop, setQuotingSop] = useState("");
  const [itemNamingGuidance, setItemNamingGuidance] = useState("");
  const [enableDemandForecasting, setEnableDemandForecasting] = useState(false);
  const [demandForecastSop, setDemandForecastSop] = useState("");
  const [extractionSchema, setExtractionSchema] = useState("");
  const [enableSetupSkill, setEnableSetupSkill] = useState(false);
  const [enableDrawingTo3d, setEnableDrawingTo3d] = useState(false);
  const [enableOpportunityThreadCreation, setEnableOpportunityThreadCreation] =
    useState(false);
  const [enableFollowUps, setEnableFollowUps] = useState(true);
  const [enableExternalFollowUps, setEnableExternalFollowUps] = useState(true);
  const [followUpIntervalsInput, setFollowUpIntervalsInput] =
    useState("1, 3, 7");
  const [maxExternalFollowUps, setMaxExternalFollowUps] = useState(3);
  const [requiresDrawings, setRequiresDrawings] = useState(false);
  const [externalCommunicationConfig, setExternalCommunicationConfig] =
    useState<ExternalCommunicationConfig>(
      DEFAULT_EXTERNAL_COMMUNICATION_CONFIG,
    );
  const [sendQuoteEmailToCustomer, setSendQuoteEmailToCustomer] =
    useState(false);
  const [notifyTeamOnWon, setNotifyTeamOnWon] = useState(true);
  const [reviewerUserIds, setReviewerUserIds] = useState<string[]>([]);
  const [quoteReviewerAssignmentMode, setQuoteReviewerAssignmentMode] =
    useState<QuoteReviewerAssignmentMode>(QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL);
  const [escalationUserIds, setEscalationUserIds] = useState<string[]>([]);
  const [ccEmailsInput, setCcEmailsInput] = useState("");
  const [quoteItemCreationPolicy, setItemCreationPolicy] =
    useState<ItemCreationPolicy>(
      QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_AND_UPDATE,
    );
  const [enableRFQExtraction, setEnableRFQExtraction] = useState(false);
  const [itemSearchEnabled, setItemSearchEnabled] = useState(false);
  const [itemSearchStrategy, setItemSearchStrategy] = useState("");

  // Email state
  const [emailProvider, setEmailProvider] = useState<EmailProvider | null>(
    null,
  );
  const [forgepointEmailPrefix, setForgepointEmailPrefix] = useState("");
  const [gmailConnectionId, setGmailConnectionId] = useState<string | null>(
    null,
  );
  const [gmailFromEmail, setGmailFromEmail] = useState("");
  const [outlookConnectionId, setOutlookConnectionId] = useState<string | null>(
    null,
  );
  const [outlookSharedFolderEmail, setOutlookSharedFolderEmail] = useState("");
  const [outlookSharedFolderId, setOutlookSharedFolderId] = useState("");
  const [agentEmailConnectionId, setAgentEmailConnectionId] = useState<
    string | null
  >(null);
  const [agentEmailFromAddress, setAgentEmailFromAddress] = useState("");
  const [smtpConnectionId, setSmtpConnectionId] = useState<string | null>(null);
  const [smtpReplyLookupImapConnectionId, setSmtpReplyLookupImapConnectionId] =
    useState<string | null>(null);

  // SMS state
  const [fromPhoneId, setFromPhoneId] = useState<string | null>(null);

  // Instant Messaging state
  const [instantMessengerConnectionId, setInstantMessengerConnectionId] =
    useState<string | null>(null);
  const [orgPhoneNumbers, setOrgPhoneNumbers] = useState<
    Array<{ id: string; phoneNumber: string }>
  >([]);

  // ── UI state ──
  const [isSaving, setIsSaving] = useState(false);
  const [timezoneOpen, setTimezoneOpen] = useState(false);
  const [emailSheetOpen, setEmailSheetOpen] = useState(false);
  const [sopFieldsSheetOpen, setSopFieldsSheetOpen] = useState(false);
  const [demandForecastSopSheetOpen, setDemandForecastSopSheetOpen] =
    useState(false);
  const [extractionDialogOpen, setExtractionDialogOpen] = useState(false);
  // ── Derived state ──
  const timezoneOptions = useMemo(() => getTimezoneList(), []);

  const lastUpdated = useMemo(() => {
    if (!settings) {
      return null;
    }
    return settings.updatedAt.toLocaleString();
  }, [settings]);

  const emailConfigValues: EmailConfigValues = useMemo(
    () => ({
      emailProvider,
      forgepointEmailPrefix,
      gmailConnectionId,
      gmailFromEmail,
      outlookConnectionId,
      outlookSharedFolderEmail,
      outlookSharedFolderId,
      agentEmailConnectionId,
      agentEmailFromAddress,
      smtpConnectionId,
      smtpReplyLookupImapConnectionId,
    }),
    [
      emailProvider,
      forgepointEmailPrefix,
      gmailConnectionId,
      gmailFromEmail,
      outlookConnectionId,
      outlookSharedFolderEmail,
      outlookSharedFolderId,
      agentEmailConnectionId,
      agentEmailFromAddress,
      smtpConnectionId,
      smtpReplyLookupImapConnectionId,
    ],
  );

  const isDirty = useMemo(() => {
    // When no settings record exists yet, compare against initial defaults
    // so users can create the first settings record for a new org
    const base = settings
      ? {
          agentName: settings.agentName ?? "",
          timezone: settings.timezone ?? DEFAULT_AGENT_TIMEZONE,
          internalEmailDomains: (settings.internalEmailDomains ?? []).join(
            ", ",
          ),
          requirements: settings.informationCollectionRequirements ?? "",
          secondaryInfo: settings.secondaryInformationToRequest ?? "",
          quotingSop: settings.quotingSop ?? "",
          itemNamingGuidance: settings.itemNamingGuidance ?? "",
          enableDemandForecasting: settings.enableDemandForecasting,
          demandForecastSop: settings.demandForecastSop ?? "",
          extractionSchema: settings.extractionReviewSchema ?? "",
          enableSetupSkill: settings.enableSetupSkill,
          enableDrawingTo3d: settings.enableDrawingTo3d,
          enableOpportunityThreadCreation:
            settings.enableOpportunityThreadCreation,
          enableFollowUps: settings.enableFollowUps,
          enableExternalFollowUps: settings.enableExternalFollowUps,
          followUpIntervals: (
            settings.externalFollowUpIntervalDays ?? [1, 3, 7]
          ).join(", "),
          maxExternalFollowUps: settings.maxExternalFollowUps ?? 3,
          requiresDrawings: settings.doesRequireTechnicalDrawings,
          externalCommunicationConfig: settings.externalCommunicationConfig,
          sendQuoteEmailToCustomer: settings.sendQuoteEmailToCustomer,
          notifyTeamOnWon: settings.notifyTeamOnWon,
          reviewerUserIds: settings.quoteReviewerUserIds ?? [],
          quoteReviewerAssignmentMode:
            settings.quoteReviewerAssignmentMode ??
            QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL,
          escalationUserIds: settings.escalationUserIds ?? [],
          ccEmails: (settings.ccEmails ?? []).join(", "),
          quoteItemCreationPolicy: settings.quoteItemCreationPolicy,
          enableRFQExtraction: settings.enableRFQExtraction,
          itemSearchEnabled: settings.itemSearchConfig?.enabled ?? false,
          itemSearchStrategy: settings.itemSearchConfig?.searchStrategy ?? "",
          emailProvider: settings.emailProvider ?? null,
          forgepointEmailPrefix: settings.forgepointEmailPrefix ?? "",
          gmailConnectionId: settings.gmailConnectionId ?? null,
          gmailFromEmail: settings.gmailFromEmail ?? "",
          outlookConnectionId: settings.microsoftOutlookConnectionId ?? null,
          outlookSharedFolderEmail:
            settings.microsoftOutlookSharedFolderEmail ?? "",
          outlookSharedFolderId: settings.microsoftOutlookSharedFolderId ?? "",
          agentEmailConnectionId: settings.agentEmailConnectionId ?? null,
          agentEmailFromAddress: settings.agentEmailFromAddress ?? "",
          smtpConnectionId: settings.smtpConnectionId ?? null,
          smtpReplyLookupImapConnectionId:
            settings.smtpReplyLookupImapConnectionId ?? null,
          fromPhoneId: settings.fromPhoneId ?? null,
          instantMessengerConnectionId:
            settings.instantMessengerConnectionId ?? null,
        }
      : {
          agentName: "",
          timezone: DEFAULT_AGENT_TIMEZONE,
          internalEmailDomains: "",
          requirements: "",
          secondaryInfo: "",
          quotingSop: "",
          itemNamingGuidance: "",
          enableDemandForecasting: false,
          demandForecastSop: "",
          extractionSchema: "",
          enableSetupSkill: false,
          enableDrawingTo3d: false,
          enableOpportunityThreadCreation: false,
          enableFollowUps: true,
          enableExternalFollowUps: true,
          followUpIntervals: "1, 3, 7",
          maxExternalFollowUps: 3,
          requiresDrawings: false,
          externalCommunicationConfig: DEFAULT_EXTERNAL_COMMUNICATION_CONFIG,
          sendQuoteEmailToCustomer: false,
          notifyTeamOnWon: true,
          reviewerUserIds: [] as string[],
          quoteReviewerAssignmentMode: QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL,
          escalationUserIds: [] as string[],
          ccEmails: "",
          quoteItemCreationPolicy:
            QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_AND_UPDATE,
          enableRFQExtraction: false,
          itemSearchEnabled: false,
          itemSearchStrategy: "",
          emailProvider: null as EmailProvider | null,
          forgepointEmailPrefix: "",
          gmailConnectionId: null as string | null,
          gmailFromEmail: "",
          outlookConnectionId: null as string | null,
          outlookSharedFolderEmail: "",
          outlookSharedFolderId: "",
          agentEmailConnectionId: null as string | null,
          agentEmailFromAddress: "",
          smtpConnectionId: null as string | null,
          smtpReplyLookupImapConnectionId: null as string | null,
          fromPhoneId: null as string | null,
          instantMessengerConnectionId: null as string | null,
        };

    if (agentName !== base.agentName) {
      return true;
    }
    if (timezone !== base.timezone) {
      return true;
    }
    if (internalEmailDomainsInput !== base.internalEmailDomains) {
      return true;
    }
    if (requirements !== base.requirements) {
      return true;
    }
    if (secondaryInformationToRequest !== base.secondaryInfo) {
      return true;
    }
    if (quotingSop !== base.quotingSop) {
      return true;
    }
    if (itemNamingGuidance !== base.itemNamingGuidance) {
      return true;
    }
    if (enableDemandForecasting !== base.enableDemandForecasting) {
      return true;
    }
    if (demandForecastSop !== base.demandForecastSop) {
      return true;
    }
    if (extractionSchema !== base.extractionSchema) {
      return true;
    }
    if (enableSetupSkill !== base.enableSetupSkill) {
      return true;
    }
    if (enableDrawingTo3d !== base.enableDrawingTo3d) {
      return true;
    }
    if (
      enableOpportunityThreadCreation !== base.enableOpportunityThreadCreation
    ) {
      return true;
    }
    if (enableFollowUps !== base.enableFollowUps) {
      return true;
    }
    if (enableExternalFollowUps !== base.enableExternalFollowUps) {
      return true;
    }
    if (followUpIntervalsInput !== base.followUpIntervals) {
      return true;
    }
    if (maxExternalFollowUps !== base.maxExternalFollowUps) {
      return true;
    }
    if (requiresDrawings !== base.requiresDrawings) {
      return true;
    }
    if (
      externalCommunicationConfig.inbound !==
        base.externalCommunicationConfig.inbound ||
      externalCommunicationConfig.outbound !==
        base.externalCommunicationConfig.outbound ||
      (externalCommunicationConfig.outboundApproval ??
        OUTBOUND_APPROVAL.NONE) !==
        (base.externalCommunicationConfig.outboundApproval ??
          OUTBOUND_APPROVAL.NONE)
    ) {
      return true;
    }
    if (sendQuoteEmailToCustomer !== base.sendQuoteEmailToCustomer) {
      return true;
    }
    if (notifyTeamOnWon !== base.notifyTeamOnWon) {
      return true;
    }
    if (
      JSON.stringify(reviewerUserIds) !== JSON.stringify(base.reviewerUserIds)
    ) {
      return true;
    }
    if (quoteReviewerAssignmentMode !== base.quoteReviewerAssignmentMode) {
      return true;
    }
    if (
      JSON.stringify(escalationUserIds) !==
      JSON.stringify(base.escalationUserIds)
    ) {
      return true;
    }
    if (ccEmailsInput !== base.ccEmails) {
      return true;
    }
    if (quoteItemCreationPolicy !== base.quoteItemCreationPolicy) {
      return true;
    }
    if (enableRFQExtraction !== base.enableRFQExtraction) {
      return true;
    }
    if (itemSearchEnabled !== base.itemSearchEnabled) {
      return true;
    }
    if (itemSearchStrategy !== base.itemSearchStrategy) {
      return true;
    }
    if (emailProvider !== base.emailProvider) {
      return true;
    }
    if (forgepointEmailPrefix !== base.forgepointEmailPrefix) {
      return true;
    }
    if (gmailConnectionId !== base.gmailConnectionId) {
      return true;
    }
    if (gmailFromEmail !== base.gmailFromEmail) {
      return true;
    }
    if (outlookConnectionId !== base.outlookConnectionId) {
      return true;
    }
    if (outlookSharedFolderEmail !== base.outlookSharedFolderEmail) {
      return true;
    }
    if (outlookSharedFolderId !== base.outlookSharedFolderId) {
      return true;
    }
    if (agentEmailConnectionId !== base.agentEmailConnectionId) {
      return true;
    }
    if (agentEmailFromAddress !== base.agentEmailFromAddress) {
      return true;
    }
    if (smtpConnectionId !== base.smtpConnectionId) {
      return true;
    }
    if (
      smtpReplyLookupImapConnectionId !== base.smtpReplyLookupImapConnectionId
    ) {
      return true;
    }
    if (fromPhoneId !== base.fromPhoneId) {
      return true;
    }
    if (instantMessengerConnectionId !== base.instantMessengerConnectionId) {
      return true;
    }

    return false;
  }, [
    settings,
    agentName,
    timezone,
    internalEmailDomainsInput,
    requirements,
    secondaryInformationToRequest,
    quotingSop,
    itemNamingGuidance,
    enableDemandForecasting,
    demandForecastSop,
    extractionSchema,
    enableSetupSkill,
    enableDrawingTo3d,
    enableOpportunityThreadCreation,
    enableFollowUps,
    enableExternalFollowUps,
    followUpIntervalsInput,
    maxExternalFollowUps,
    requiresDrawings,
    externalCommunicationConfig,
    sendQuoteEmailToCustomer,
    notifyTeamOnWon,
    reviewerUserIds,
    quoteReviewerAssignmentMode,
    escalationUserIds,
    ccEmailsInput,
    quoteItemCreationPolicy,
    enableRFQExtraction,
    itemSearchEnabled,
    itemSearchStrategy,
    emailProvider,
    forgepointEmailPrefix,
    gmailConnectionId,
    gmailFromEmail,
    outlookConnectionId,
    outlookSharedFolderEmail,
    outlookSharedFolderId,
    agentEmailConnectionId,
    agentEmailFromAddress,
    smtpConnectionId,
    smtpReplyLookupImapConnectionId,
    fromPhoneId,
    instantMessengerConnectionId,
  ]);

  const hasAgentEmail =
    emailProvider === EMAIL_PROVIDER.AGENT_EMAIL &&
    agentEmailConnectionId !== null &&
    agentEmailFromAddress.trim().length > 0;
  const isOutboundApprovalOn =
    externalCommunicationConfig.outboundApproval === OUTBOUND_APPROVAL.EXTERNAL;

  // ── Effects ──
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    fetchOrganization().catch(() => undefined);
  }, [fetchOrganization]);

  useEffect(() => {
    fetchApi("phone-numbers", "GET").then((res) => {
      if (res.ok && Array.isArray(res.data)) {
        setOrgPhoneNumbers(
          res.data as Array<{ id: string; phoneNumber: string }>,
        );
      }
    });
  }, []);

  /**
   * Resets all form fields to match the current stored settings.
   * Used on initial load and when discarding unsaved changes.
   */
  const syncFormFromSettings = useCallback(() => {
    if (!settings) {
      return;
    }
    setAgentName(settings.agentName ?? "");
    setTimezone(settings.timezone ?? DEFAULT_AGENT_TIMEZONE);
    setInternalEmailDomainsInput(
      (settings.internalEmailDomains ?? []).join(", "),
    );
    setRequirements(settings.informationCollectionRequirements ?? "");
    setSecondaryInformationToRequest(
      settings.secondaryInformationToRequest ?? "",
    );
    setQuotingSop(settings.quotingSop ?? "");
    setItemNamingGuidance(settings.itemNamingGuidance ?? "");
    setEnableDemandForecasting(settings.enableDemandForecasting);
    setDemandForecastSop(settings.demandForecastSop ?? "");
    setExtractionSchema(settings.extractionReviewSchema ?? "");
    setEnableSetupSkill(settings.enableSetupSkill);
    setEnableDrawingTo3d(settings.enableDrawingTo3d);
    setEnableOpportunityThreadCreation(
      settings.enableOpportunityThreadCreation,
    );
    setEnableFollowUps(settings.enableFollowUps);
    setEnableExternalFollowUps(settings.enableExternalFollowUps);
    setFollowUpIntervalsInput(
      (settings.externalFollowUpIntervalDays ?? [1, 3, 7]).join(", "),
    );
    setMaxExternalFollowUps(settings.maxExternalFollowUps ?? 3);
    setRequiresDrawings(settings.doesRequireTechnicalDrawings);
    setExternalCommunicationConfig(settings.externalCommunicationConfig);
    setSendQuoteEmailToCustomer(settings.sendQuoteEmailToCustomer);
    setNotifyTeamOnWon(settings.notifyTeamOnWon);
    setReviewerUserIds(settings.quoteReviewerUserIds ?? []);
    setQuoteReviewerAssignmentMode(
      settings.quoteReviewerAssignmentMode ??
        QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL,
    );
    setEscalationUserIds(settings.escalationUserIds ?? []);
    setCcEmailsInput((settings.ccEmails ?? []).join(", "));
    setItemCreationPolicy(settings.quoteItemCreationPolicy);
    setEnableRFQExtraction(settings.enableRFQExtraction);
    setItemSearchEnabled(settings.itemSearchConfig?.enabled ?? false);
    setItemSearchStrategy(settings.itemSearchConfig?.searchStrategy ?? "");
    setEmailProvider(settings.emailProvider ?? null);
    setForgepointEmailPrefix(settings.forgepointEmailPrefix ?? "");
    setGmailConnectionId(settings.gmailConnectionId ?? null);
    setGmailFromEmail(settings.gmailFromEmail ?? "");
    setOutlookConnectionId(settings.microsoftOutlookConnectionId ?? null);
    setOutlookSharedFolderEmail(
      settings.microsoftOutlookSharedFolderEmail ?? "",
    );
    setOutlookSharedFolderId(settings.microsoftOutlookSharedFolderId ?? "");
    setAgentEmailConnectionId(settings.agentEmailConnectionId ?? null);
    setAgentEmailFromAddress(settings.agentEmailFromAddress ?? "");
    setSmtpConnectionId(settings.smtpConnectionId ?? null);
    setSmtpReplyLookupImapConnectionId(
      settings.smtpReplyLookupImapConnectionId ?? null,
    );
    setFromPhoneId(settings.fromPhoneId ?? null);
    setInstantMessengerConnectionId(
      settings.instantMessengerConnectionId ?? null,
    );
  }, [settings]);

  useEffect(() => {
    syncFormFromSettings();
  }, [syncFormFromSettings]);

  // ── Handlers ──
  const handleEmailConfigApply = (values: EmailConfigValues) => {
    setEmailProvider(values.emailProvider);
    setForgepointEmailPrefix(values.forgepointEmailPrefix);
    setGmailConnectionId(values.gmailConnectionId);
    setGmailFromEmail(values.gmailFromEmail);
    setOutlookConnectionId(values.outlookConnectionId);
    setOutlookSharedFolderEmail(values.outlookSharedFolderEmail);
    setOutlookSharedFolderId(values.outlookSharedFolderId);
    setAgentEmailConnectionId(values.agentEmailConnectionId);
    setAgentEmailFromAddress(values.agentEmailFromAddress);
    setSmtpConnectionId(values.smtpConnectionId);
    setSmtpReplyLookupImapConnectionId(values.smtpReplyLookupImapConnectionId);
  };

  const save = async () => {
    setIsSaving(true);

    const trimmedName = agentName.trim();
    const trimmedSchema = extractionSchema.trim();
    const internalEmailDomains = parseInternalEmailDomains(
      internalEmailDomainsInput,
    );
    const ccEmails = parseEmailList(ccEmailsInput);

    const payload: UpsertStandardAgentSettingsBody = {
      agentName: trimmedName.length > 0 ? trimmedName : "Frank",
      timezone,
      internalEmailDomains,
      enableSetupSkill,
      enableDrawingTo3d,
      enableOpportunityThreadCreation,
      enableFollowUps,
      enableExternalFollowUps,
      externalFollowUpIntervalDays: parseIntervalDays(followUpIntervalsInput),
      maxExternalFollowUps,
      externalCommunicationConfig,
      informationCollectionRequirements: requirements.trim(),
      secondaryInformationToRequest: secondaryInformationToRequest.trim(),
      doesRequireTechnicalDrawings: requiresDrawings,
      sendQuoteEmailToCustomer,
      notifyTeamOnWon,
      quotingSop: quotingSop.trim(),
      itemNamingGuidance: itemNamingGuidance.trim() || null,
      enableDemandForecasting,
      demandForecastSop: demandForecastSop.trim(),
      ccEmails,
      extractionReviewSchema:
        requiresDrawings && trimmedSchema.length > 0 ? trimmedSchema : null,
      quoteReviewerUserIds: reviewerUserIds,
      quoteReviewerAssignmentMode,
      escalationUserIds,
      quoteItemCreationPolicy,
      enableRFQExtraction,
      itemSearchConfig: itemSearchEnabled
        ? { enabled: true, searchStrategy: itemSearchStrategy }
        : null,
      emailProvider,
      forgepointEmailPrefix:
        emailProvider === EMAIL_PROVIDER.FORGEPOINT_MAIL
          ? forgepointEmailPrefix.trim() || null
          : null,
      gmailConnectionId:
        emailProvider === EMAIL_PROVIDER.GMAIL ? gmailConnectionId : null,
      gmailFromEmail:
        emailProvider === EMAIL_PROVIDER.GMAIL
          ? gmailFromEmail.trim() || null
          : null,
      microsoftOutlookConnectionId:
        emailProvider === EMAIL_PROVIDER.MICROSOFT_OUTLOOK
          ? outlookConnectionId
          : null,
      microsoftOutlookSharedFolderEmail:
        emailProvider === EMAIL_PROVIDER.MICROSOFT_OUTLOOK
          ? outlookSharedFolderEmail.trim() || null
          : null,
      microsoftOutlookSharedFolderId:
        emailProvider === EMAIL_PROVIDER.MICROSOFT_OUTLOOK
          ? outlookSharedFolderId.trim() || null
          : null,
      agentEmailConnectionId:
        emailProvider === EMAIL_PROVIDER.AGENT_EMAIL
          ? agentEmailConnectionId
          : null,
      agentEmailFromAddress:
        emailProvider === EMAIL_PROVIDER.AGENT_EMAIL
          ? agentEmailFromAddress.trim() || null
          : null,
      smtpConnectionId:
        emailProvider === EMAIL_PROVIDER.SMTP ? smtpConnectionId : null,
      smtpReplyLookupImapConnectionId:
        emailProvider === EMAIL_PROVIDER.SMTP
          ? smtpReplyLookupImapConnectionId
          : null,
      fromPhoneId,
      instantMessengerConnectionId,
    };

    const success = await updateSettings(payload);
    setIsSaving(false);

    if (success) {
      toast.success("Agent settings updated successfully");
      return;
    }

    toast.error("Failed to update agent settings");
  };

  // ── Early returns ──
  if (isLoading) {
    return <SettingsSkeleton />;
  }

  if (error) {
    return (
      <Card className="border-destructive/40 shadow-sm">
        <CardContent className="py-6 text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <fieldset disabled={!canEdit} className="min-w-0 space-y-6 pt-4">
        {/* ── General ── */}
        <div className="flex items-center justify-between">
          <SectionLabel title="General" />
          {lastUpdated && (
            <p className="text-xs text-muted-foreground">
              Last updated: {lastUpdated}
            </p>
          )}
        </div>
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              id="agent-settings-agent-name"
              label="Agent Name"
              description="The name your agent uses in conversations."
            >
              <Input
                className="w-[200px]"
                value={agentName}
                onChange={(e) => setAgentName(e.target.value)}
              />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-timezone"
              label="Timezone"
              description="Used for scheduling and time references."
            >
              <Popover open={timezoneOpen} onOpenChange={setTimezoneOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={timezoneOpen}
                    className="w-[220px] justify-between"
                  >
                    <span className="truncate">{timezone}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[320px] p-0" align="end">
                  <Command>
                    <CommandInput placeholder="Search timezone..." />
                    <CommandEmpty>No timezone found.</CommandEmpty>
                    <CommandList>
                      {timezoneOptions.map((tz) => (
                        <CommandItem
                          key={tz}
                          value={tz}
                          onSelect={(v) => {
                            setTimezone(v);
                            setTimezoneOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              timezone === tz ? "opacity-100" : "opacity-0",
                            )}
                          />
                          {tz}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </SettingsRow>

            <SettingsRow
              id="agent-settings-internal-email-domains"
              label="Internal Email Domains"
              description="Comma-separated domains treated as internal senders."
            >
              <Input
                className="w-[260px]"
                value={internalEmailDomainsInput}
                onChange={(e) => setInternalEmailDomainsInput(e.target.value)}
                placeholder="forgepoint.ai, yourcompany.com"
              />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-escalation-contacts"
              label="Escalation Contacts"
              description="Users notified when the agent cannot handle a request."
            >
              <div className="w-[260px] space-y-1.5">
                <UserSelector
                  mode="multi"
                  selectedUserIds={escalationUserIds}
                  onUserIdsChange={setEscalationUserIds}
                  placeholder="Select contacts"
                />
                {!isExternalInboundEnabled(externalCommunicationConfig) &&
                  escalationUserIds.length === 0 && (
                    <p className="animate-in fade-in-0 slide-in-from-top-1 duration-300 text-xs text-warning">
                      No escalation contacts selected. Escalation emails will
                      default to organization admins.
                    </p>
                  )}
              </div>
            </SettingsRow>

            <SettingsRow
              id="agent-settings-email"
              label="Email"
              description="Email provider for sending and receiving."
            >
              <div className="flex items-center gap-2">
                {emailProvider ? (
                  <Badge variant="secondary">
                    {EMAIL_PROVIDER_LABELS[emailProvider]}
                  </Badge>
                ) : (
                  <span className="text-xs text-muted-foreground/60">
                    Not configured
                  </span>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEmailSheetOpen(true)}
                >
                  Configure
                </Button>
              </div>
            </SettingsRow>

            <SettingsRow
              id="agent-settings-sms"
              label="SMS"
              description="Phone number for outbound SMS messages."
            >
              <Select
                value={fromPhoneId ?? "none"}
                onValueChange={(v) => setFromPhoneId(v === "none" ? null : v)}
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Select phone number" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {orgPhoneNumbers.map((phone) => (
                    <SelectItem key={phone.id} value={phone.id}>
                      {phone.phoneNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsRow>

            {/* ── Instant Messaging ── */}
            <SettingsRow
              label="Instant Messaging"
              description="Connection used for sending and receiving instant messages."
            >
              <div className="flex items-center gap-2">
                <div className="w-[220px]">
                  <ConnectionSelector
                    appId="slack"
                    providerId="slack"
                    connectionId={instantMessengerConnectionId}
                    onSelect={setInstantMessengerConnectionId}
                  />
                </div>
                {instantMessengerConnectionId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setInstantMessengerConnectionId(null)}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </SettingsRow>
          </div>
        </Card>

        {/* ── Quoting ── */}
        <SectionLabel title="Quoting" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            {isExternalOutboundEnabled(externalCommunicationConfig) && (
              <SettingsRow
                id="agent-settings-external-follow-ups"
                label="External Follow Ups"
                description="Follow up with customers after sending a quote."
                expandedContent={
                  enableExternalFollowUps ? (
                    <div className="flex items-end gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Intervals (business days)
                        </Label>
                        <Input
                          className="w-[140px] h-8 text-sm"
                          value={followUpIntervalsInput}
                          onChange={(e) =>
                            setFollowUpIntervalsInput(e.target.value)
                          }
                          placeholder="1, 3, 7"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Maximum
                        </Label>
                        <Input
                          className="w-[80px] h-8 text-sm"
                          type="number"
                          min={1}
                          max={10}
                          value={maxExternalFollowUps}
                          onChange={(e) =>
                            setMaxExternalFollowUps(
                              Math.max(
                                1,
                                Math.min(10, parseInt(e.target.value, 10) || 1),
                              ),
                            )
                          }
                        />
                      </div>
                    </div>
                  ) : undefined
                }
              >
                <Switch
                  checked={enableExternalFollowUps}
                  onCheckedChange={setEnableExternalFollowUps}
                />
              </SettingsRow>
            )}

            <SettingsRow
              label="Technical Drawings Required"
              description="Require drawing-based review for quoting."
            >
              <Switch
                checked={requiresDrawings}
                onCheckedChange={setRequiresDrawings}
              />
            </SettingsRow>

            <SettingsRow
              label="Outbound Reach"
              description="How far out the agent may send email and SMS."
            >
              <Select
                value={externalCommunicationConfig.outbound}
                onValueChange={(value: CommunicationScope) =>
                  setExternalCommunicationConfig((prev) => ({
                    ...prev,
                    outbound: value,
                  }))
                }
              >
                <SelectTrigger className="w-[200px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMUNICATION_SCOPE_VALUES.map((scope) => (
                    <SelectItem key={scope} value={scope}>
                      {COMMUNICATION_SCOPE_LABELS[scope]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsRow>

            {isExternalOutboundEnabled(externalCommunicationConfig) && (
              <SettingsRow
                label="Require approval for external email"
                description="The agent drafts external emails as tasks and sends them from its agent email connection once someone approves the exact message. Other email send methods cannot reach external recipients while this is on."
                expandedContent={
                  !hasAgentEmail && (
                    <p className="text-xs text-muted-foreground">
                      Set up Agent Email with a send-from address to turn this
                      on. Approved email is sent through it.
                    </p>
                  )
                }
              >
                <Switch
                  checked={isOutboundApprovalOn}
                  disabled={!hasAgentEmail && !isOutboundApprovalOn}
                  onCheckedChange={(checked) =>
                    setExternalCommunicationConfig((prev) => ({
                      ...prev,
                      outboundApproval: checked
                        ? OUTBOUND_APPROVAL.EXTERNAL
                        : OUTBOUND_APPROVAL.NONE,
                    }))
                  }
                />
              </SettingsRow>
            )}

            <SettingsRow
              label="Inbound Reach"
              description="How far out the agent may receive and act on email and SMS."
            >
              <Select
                value={externalCommunicationConfig.inbound}
                onValueChange={(value: CommunicationScope) =>
                  setExternalCommunicationConfig((prev) => ({
                    ...prev,
                    inbound: value,
                  }))
                }
              >
                <SelectTrigger className="w-[200px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COMMUNICATION_SCOPE_VALUES.map((scope) => (
                    <SelectItem key={scope} value={scope}>
                      {COMMUNICATION_SCOPE_LABELS[scope]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingsRow>

            <SettingsRow
              label="Email Customer Quotes"
              description="Allow sending quote emails directly to customers."
            >
              <Switch
                checked={sendQuoteEmailToCustomer}
                onCheckedChange={setSendQuoteEmailToCustomer}
              />
            </SettingsRow>

            <SettingsRow
              label="Notify Team on Won"
              description="Send a congratulations email to the team when a deal is won."
            >
              <Switch
                checked={notifyTeamOnWon}
                onCheckedChange={setNotifyTeamOnWon}
              />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-rfq-extraction"
              label="RFQ Extraction"
              description="AI-powered extraction of line items from PDF/image RFQ attachments."
            >
              <Switch
                checked={enableRFQExtraction}
                onCheckedChange={setEnableRFQExtraction}
              />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-item-creation-policy"
              label="Item Creation Policy"
              description="Controls item creation and modification during quoting."
            >
              <Select
                value={quoteItemCreationPolicy}
                onValueChange={(v) =>
                  setItemCreationPolicy(v as ItemCreationPolicy)
                }
              >
                <SelectTrigger className="w-[260px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value={QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_AND_UPDATE}
                  >
                    Allow Create + Update
                  </SelectItem>
                  <SelectItem
                    value={
                      QUOTE_ITEM_CREATION_POLICY.ALLOW_CREATE_UPDATE_AND_MATERIALS
                    }
                  >
                    Allow Create + Update + Materials
                  </SelectItem>
                  <SelectItem
                    value={QUOTE_ITEM_CREATION_POLICY.PRICE_MODEL_ONLY}
                  >
                    Price Model Only
                  </SelectItem>
                  <SelectItem
                    value={QUOTE_ITEM_CREATION_POLICY.NO_CREATE_OR_UPDATE}
                  >
                    No Create or Update
                  </SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>

            <SettingsRow
              id="agent-settings-quote-reviewers"
              label="Quote Finalization Reviewers"
              description="Users who must approve quotes before they are finalized."
            >
              <div className="w-[260px]">
                <UserSelector
                  mode="multi"
                  selectedUserIds={reviewerUserIds}
                  onUserIdsChange={setReviewerUserIds}
                  placeholder="Select reviewers"
                />
              </div>
            </SettingsRow>

            <SettingsRow
              label="Reviewer Requirement"
              description="Controls who must be assigned to final quote review tasks."
            >
              <div className="w-[260px]">
                <Select
                  value={quoteReviewerAssignmentMode}
                  onValueChange={(value) => {
                    if (
                      value === QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL ||
                      value === QUOTE_REVIEWER_ASSIGNMENT_MODE.ONE_OF
                    ) {
                      setQuoteReviewerAssignmentMode(value);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={QUOTE_REVIEWER_ASSIGNMENT_MODE.ALL}>
                      All
                    </SelectItem>
                    <SelectItem value={QUOTE_REVIEWER_ASSIGNMENT_MODE.ONE_OF}>
                      One of
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </SettingsRow>

            <SettingsRow
              id="agent-settings-quote-cc-emails"
              label="Quote CC Emails"
              description="Emails always CC'd on quote communications."
            >
              <Input
                className="w-[260px]"
                value={ccEmailsInput}
                onChange={(e) => setCcEmailsInput(e.target.value)}
                placeholder="cc1@company.com, cc2@company.com"
              />
            </SettingsRow>

            {requiresDrawings && (
              <SettingsRow
                label="Extraction Review Schema"
                description="Schema for technical drawing extraction review."
              >
                <div className="flex items-center gap-2">
                  <ConfiguredIndicator
                    configured={extractionSchema.length > 0}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExtractionDialogOpen(true)}
                  >
                    <Pencil className="mr-1.5 h-3 w-3" />
                    Edit
                  </Button>
                </div>
              </SettingsRow>
            )}
          </div>

          {/* System-Managed sub-section */}
          <div className="border-t px-4 py-3.5">
            <div className="flex items-start justify-between gap-4">
              <div className="space-y-1">
                <h4 className="text-sm font-semibold">System-Managed</h4>
                {settings?.sopVersion && (
                  <p className="text-xs text-muted-foreground">
                    Configured automatically. Manual edits made here will be
                    overwritten on the next system update.
                  </p>
                )}
                {settings && (
                  <div className="pt-1">
                    <SopSyncBanner
                      sopVersion={settings.sopVersion}
                      sopUpdatedBy={settings.sopUpdatedBy}
                      updatedAt={settings.updatedAt}
                    />
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSopFieldsSheetOpen(true)}
              >
                <Pencil className="mr-1.5 h-3 w-3" />
                Edit
              </Button>
            </div>
          </div>

          <div className="divide-y divide-dashed border-t border-dashed text-muted-foreground">
            <SettingsRow
              label="Information Collection Requirements"
              description="Checklist the agent must collect before creating a quote."
            >
              <ConfiguredIndicator configured={requirements.length > 0} />
            </SettingsRow>

            <SettingsRow
              label="Secondary Information to Request"
              description="Additional information to request from customers."
            >
              <ConfiguredIndicator
                configured={secondaryInformationToRequest.length > 0}
              />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-quoting-sop"
              label="Quoting SOP"
              description="Standard operating procedure for the quoting workflow."
            >
              <ConfiguredIndicator configured={quotingSop.length > 0} />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-item-naming-guidance"
              label="Item Naming Guidance"
              description="How the agent names items. Blank uses the default part-number convention."
            >
              <ConfiguredIndicator configured={itemNamingGuidance.length > 0} />
            </SettingsRow>

            <SettingsRow
              id="agent-settings-item-search-subagent"
              label="Item Search Sub-Agent"
              description="Dedicated sub-agent for catalog item searches."
            >
              <ConfiguredIndicator configured={itemSearchEnabled} />
            </SettingsRow>

            {itemSearchEnabled && (
              <SettingsRow
                label="Item Search Strategy"
                description="Custom search rules and item matching instructions."
              >
                <ConfiguredIndicator
                  configured={itemSearchStrategy.length > 0}
                />
              </SettingsRow>
            )}
          </div>
        </Card>

        {demandForecastingFeatureEnabled && (
          <>
            {/* ── Demand Forecasting ── */}
            <SectionLabel title="Demand Forecasting Skill" />
            <Card className="shadow-sm overflow-hidden">
              <div className="divide-y">
                <SettingsRow
                  id="agent-settings-demand-forecast-enabled"
                  label="Demand Forecast Enabled"
                  description="Allow the agent to create, update, and report on demand forecasts."
                >
                  <Switch
                    checked={enableDemandForecasting}
                    onCheckedChange={setEnableDemandForecasting}
                  />
                </SettingsRow>

                {enableDemandForecasting && (
                  <SettingsRow
                    id="agent-settings-demand-forecast-sop"
                    label="Demand Forecast SOP"
                    description="Standard operating procedure for translating quote lines into demand forecasts."
                  >
                    <div className="flex items-center gap-2">
                      <ConfiguredIndicator
                        configured={demandForecastSop.length > 0}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDemandForecastSopSheetOpen(true)}
                      >
                        <Pencil className="mr-1.5 h-3 w-3" />
                        Edit
                      </Button>
                    </div>
                  </SettingsRow>
                )}
              </div>
            </Card>
          </>
        )}

        {/* ── Additional Skills ── */}
        <SectionLabel title="Additional Skills" />
        <Card className="shadow-sm overflow-hidden">
          <div className="divide-y">
            <SettingsRow
              label="Setup Skill"
              description="Onboarding and configuration workflow. Recommended to disable once setup is complete."
            >
              <Switch
                checked={enableSetupSkill}
                onCheckedChange={setEnableSetupSkill}
              />
            </SettingsRow>
            <SettingsRow
              label="Drawing to 3D"
              description="Converts 2D engineering drawing PDFs into 3D STEP files using CadQuery."
            >
              <Switch
                checked={enableDrawingTo3d}
                onCheckedChange={setEnableDrawingTo3d}
              />
            </SettingsRow>
            <SettingsRow
              label="Opportunity threads"
              description="Lets the agent start a separate conversation for each opportunity when one RFQ covers several."
            >
              <Switch
                checked={enableOpportunityThreadCreation}
                onCheckedChange={setEnableOpportunityThreadCreation}
              />
            </SettingsRow>
            <SettingsRow
              label="Follow-ups"
              description="Allows automated follow-up behavior for the agent."
            >
              <Switch
                checked={enableFollowUps}
                onCheckedChange={setEnableFollowUps}
              />
            </SettingsRow>
          </div>
        </Card>

        {/* ── Triggers ── */}
        <StandardAgentTriggersCard />

        <UnsavedChangesBar
          isDirty={isDirty}
          onSave={save}
          onDiscard={syncFormFromSettings}
          isSaving={isSaving}
        />
      </fieldset>

      {/* ── Sheets & Dialogs ── */}
      <EmailConfigSheet
        open={emailSheetOpen}
        onOpenChange={setEmailSheetOpen}
        initialValues={emailConfigValues}
        onApply={handleEmailConfigApply}
        organizationSlug={organizationSlug}
      />

      <SopFieldsSheet
        open={sopFieldsSheetOpen}
        onOpenChange={setSopFieldsSheetOpen}
        values={{
          informationCollectionRequirements: requirements,
          secondaryInformationToRequest,
          quotingSop,
          itemNamingGuidance,
          itemSearchEnabled,
          itemSearchStrategy,
        }}
        onApply={(v) => {
          setRequirements(v.informationCollectionRequirements);
          setSecondaryInformationToRequest(v.secondaryInformationToRequest);
          setQuotingSop(v.quotingSop);
          setItemNamingGuidance(v.itemNamingGuidance);
          setItemSearchEnabled(v.itemSearchEnabled);
          setItemSearchStrategy(v.itemSearchStrategy);
        }}
        sopVersion={settings?.sopVersion ?? null}
        sopUpdatedBy={settings?.sopUpdatedBy ?? null}
        updatedAt={settings?.updatedAt ?? new Date()}
      />

      <TextSettingSheet
        open={demandForecastSopSheetOpen}
        onOpenChange={setDemandForecastSopSheetOpen}
        title="Demand Forecast SOP"
        description="Instructions the agent follows when translating quote lines into demand forecasts."
        value={demandForecastSop}
        onApply={setDemandForecastSop}
        placeholder="Describe how quote lines should become demand forecasts..."
        headerContent={
          <SopSyncBanner
            sopVersion={settings?.sopVersion ?? null}
            sopUpdatedBy={settings?.sopUpdatedBy ?? null}
            updatedAt={settings?.updatedAt ?? new Date()}
          />
        }
      />

      <ExtractionSchemaDialog
        open={extractionDialogOpen}
        onOpenChange={setExtractionDialogOpen}
        value={extractionSchema}
        onApply={setExtractionSchema}
      />
    </>
  );
}

// ── Local Components ──

/**
 * Skeleton loading state that mirrors the layout of the settings page.
 * Shows placeholder rows for General, Quoting, and Additional Skills sections.
 * @returns Skeleton layout element
 */
function SettingsSkeleton() {
  return (
    <div className="space-y-6">
      {/* General section */}
      <Skeleton className="h-4 w-16" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonRow key={`general-${i}`} />
          ))}
        </div>
      </Card>

      {/* Quoting section */}
      <Skeleton className="h-4 w-16" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 10 }).map((_, i) => (
            <SkeletonRow key={`quoting-${i}`} />
          ))}
        </div>
      </Card>

      {/* Additional Skills section */}
      <Skeleton className="h-4 w-28" />
      <Card className="shadow-sm overflow-hidden">
        <div className="divide-y">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonRow key={`skills-${i}`} />
          ))}
        </div>
      </Card>
    </div>
  );
}

/**
 * Single skeleton row matching the SettingsRow layout.
 * @returns Skeleton row element
 */
function SkeletonRow() {
  return (
    <div className="flex items-center justify-between gap-4 px-4 py-3.5">
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-8 w-[200px] rounded-md" />
    </div>
  );
}

/**
 * Standalone section label rendered above a Card as a lightweight heading.
 * @param props - Contains a title string
 * @returns Section label element
 */
function SectionLabel({ title }: { title: string }) {
  return <h3 className="text-lg font-semibold tracking-tight pl-2">{title}</h3>;
}

/**
 * Small status indicator showing whether a text-based setting has been configured.
 * Displays a green dot with "Configured" or muted "Not set" text.
 * @param props - Contains a configured boolean
 * @returns Status indicator element
 */
function ConfiguredIndicator({ configured }: { configured: boolean }) {
  if (configured) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span className="h-1.5 w-1.5 rounded-full bg-success" />
        Configured
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground/50">Not set</span>;
}

// ── Utility Functions ──

/**
 * Parses a comma-separated string of email domains into a unique array.
 * @param input - Comma-separated domain string
 * @returns Array of unique, trimmed, lowercased domain strings
 */
function parseInternalEmailDomains(input: string): string[] {
  const uniqueDomains = new Set(
    input
      .split(",")
      .map((domain) => domain.trim().toLowerCase())
      .filter((domain) => domain.length > 0),
  );
  return Array.from(uniqueDomains);
}

/**
 * Parses a comma-separated string of emails into a unique array.
 * @param input - Comma-separated email string
 * @returns Array of unique, trimmed, lowercased email strings
 */
function parseEmailList(input: string): string[] {
  const uniqueEmails = new Set(
    input
      .split(",")
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0),
  );
  return Array.from(uniqueEmails);
}

/**
 * Parses a comma-separated string of interval days into an array of positive integers.
 * @param input - Comma-separated number string
 * @returns Array of positive integer interval days
 */
function parseIntervalDays(input: string): number[] {
  return input
    .split(",")
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n) && n >= 1);
}
